import { FC, Fragment, useEffect, useRef, useState } from 'react'
import {
    Box, Button, Checkbox, CircularProgress, FormControlLabel,
    Grid, IconButton, InputAdornment, Link, TextField, Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import QRCode from 'react-qr-code'
import { InputProps } from '../../types'
import { Connect, mapDispatchToProps } from '../../Redux'
import { Apis } from '../../helpers'
import queryString from 'query-string'
import logo from '../../assets/images/logo.png'
import {
    RemoveRedEyeOutlined, VisibilityOffOutlined,
    CheckCircleOutline, ArrowBackOutlined,
} from '@mui/icons-material'
import { EventsEmit, Events } from '../../configs/events'
import { sendData } from '../../helpers/socket'
import { EventRegister } from 'react-native-event-listeners'
import NoData from '../Common/NoData'
import { useNavigate } from 'react-router-dom'

const PRIMARY = '#8c7c0c'
const PRIMARY_DARK = '#6b5e09'

const SIDE_FEATURES = [
    { icon: '🏠', text: 'Manage all your properties in one place' },
    { icon: '💳', text: 'Collect rent & track payments instantly' },
    { icon: '📊', text: 'Powerful analytics and investment insights' },
]

interface StateProps {
    currentPage: 'Sign In' | 'Sign Up' | 'Sign Up Success' | 'Forgot Password' | 'Reset Password'
    inputs: InputProps[]
    loading: boolean
    submitting: boolean
    showPassword: boolean
    processing: boolean
    signUpError: string | null
    qrSessionId: string | null
}

interface Props {
    setRedux?: (data: any) => void
}

const Auth: FC<Props> = ({ setRedux }) => {
    const initialInputs: InputProps[] = [
        { label: 'First Name', value: '', field: 'firstName' },
        { label: 'Last Name', value: '', field: 'lastName' },
        { label: 'Email', value: '', type: 'email', field: 'email' },
        { label: 'Username', value: '', field: 'userName' },
        { label: 'Password', value: '', field: 'password', type: 'password' },
        { label: 'New Password', value: '', field: 'newPassword', type: 'password' },
        { label: 'Confirm Password', value: '', field: 'confirmPassword', type: 'password' },
        { label: 'Agree to ', value: false, field: 'agreeToTerms', type: 'checkbox' },
    ]

    const [state, setState] = useState<StateProps>({
        currentPage: 'Sign In',
        inputs: initialInputs,
        loading: false,
        submitting: false,
        showPassword: false,
        signUpError: null,
        processing: true,
        qrSessionId: null,
    })

    const {
        currentPage, loading, inputs, submitting, showPassword,
        signUpError, processing, qrSessionId,
    } = state

    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const navigate = useNavigate()

    const showInputs = inputs.filter(q => (
        currentPage === 'Sign In' ? ['email', 'password'] :
        currentPage === 'Forgot Password' ? ['email'] :
        currentPage === 'Reset Password' ? ['newPassword', 'confirmPassword'] :
            initialInputs.map(q => q.field).filter(q => !['newPassword', 'confirmPassword'].includes(q))
    ).includes(q.field))

    const changeInput = (input: string, obj: any) => {
        const Inputs = [...inputs]
        const inputIndex = inputs.findIndex(q => q.field === input)
        Inputs[inputIndex] = { ...Inputs[inputIndex], ...obj }
        setState({ ...state, inputs: Inputs, submitting: false })
    }

    const stopPolling = () => {
        if (pollRef.current) clearInterval(pollRef.current)
    }

    const clearQrTimers = () => {
        stopPolling()
        if (setRedux) setRedux({ qrSessionId: null })
    }

    const startQrSession = () => {
        stopPolling()

        const sessionId = crypto.randomUUID()
        setState(s => ({ ...s, qrSessionId: sessionId }))
        if (setRedux) setRedux({ qrSessionId: sessionId })

        sendData({ action: 'userConnected', sessionId })
        Apis({ module: 'Users', action: 'registerQRSession', sessionId }).catch(() => {})

        pollRef.current = setInterval(() => {
            Apis({ module: 'Users', action: 'checkQRSession', sessionId })
                .then((r: any) => {
                    if (r?.authenticated) {
                        clearQrTimers()
                        if (setRedux) setRedux({ ...r })
                        navigate('/properties')
                    }
                })
                .catch(() => stopPolling())
        }, 3000)
    }

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const data = new FormData(event.currentTarget)
        const obj: any = {}
        data.forEach((v, k) => (obj[k] = v))
        if (currentPage === 'Sign Up' && !obj.agreeToTerms)
            changeInput('agreeToTerms', { error: 'You must agree to the terms and conditions of using this software.' })
        else if (currentPage === 'Reset Password' && obj.newPassword !== obj.confirmPassword)
            changeInput('confirmPassword', { error: 'Passwords do not match.' })
        else {
            obj.agreeToTerms = true
            setState({ ...state, submitting: true })
            Apis(
                { module: 'Users', action: currentPage, ...obj },
                queryString.parse(window.location.search)?.reset_password_token as string
            ).then((resp: any) => {
                const { error, nextPage, field, message, status } = resp
                if (error) changeInput(field, { error })
                else if (nextPage) setState({ ...state, currentPage: nextPage })
                else {
                    setState({ ...state, submitting: false })
                    if (setRedux) setRedux({ ...resp })
                    navigate('/properties')
                }
                if (message) EventsEmit('app', { info: { message, status } })
                if (['Reset Password', 'Sign In'].includes(currentPage)) {
                    const url = window.location.href
                    const updatedUrl = url.replace(/([&?])reset_password_token=[^&]+(&|$)/, '$1')
                    window.history.replaceState({}, document.title, updatedUrl)
                }
            }).catch(() => {
                EventsEmit('app', { info: { message: 'Something went wrong! Please check your internet connection.', status: 'error' } })
                setState({ ...state, submitting: false })
            })
        }
    }

    const activateAccount = (user_access: string, action: string) => {
        Apis({ module: 'Users', action, user_access })
            .then((resp: any) => {
                const { error, message, nextPage } = resp
                if (error) setState({ ...state, currentPage: 'Sign Up Success', signUpError: message })
                else if (nextPage) setState({ ...state, currentPage: nextPage })
                else {
                    if (setRedux) setRedux({ ...resp })
                    setState({ ...state, loading: false, currentPage: 'Sign In' })
                    navigate('/properties')
                }
            })
            .catch(() => setState({ ...state, loading: false, currentPage: 'Sign In' }))
    }

    useEffect(() => {
        const query = queryString.parse(window.location.search)
        if (query.user_access || query.reset_password_token) {
            setState({ ...state, loading: true })
            activateAccount(
                (query.user_access || query.reset_password_token) as string,
                query.user_access ? 'activateUserAccount' : 'checkForgotPasswordRequest'
            )
        } else if (query.plan) {
            const { features } = query
            if (setRedux)
                setRedux({ loginSelectedPlan: { plan: query.plan, features: features ? (features as string).split(',') : [] } })
        } else if (query.signup) {
            setState({ ...state, currentPage: 'Sign Up', processing: false })
        }
        if (!query.signup) setTimeout(() => {
            setState(s => ({ ...s, processing: false }))
            startQrSession()
        }, 2000)

        const socketListener = EventRegister.addEventListener('socket', (data: any) => {
            if (data?.action === 'qrLogin') {
                clearQrTimers()
                const { action, ...authData } = data
                if (setRedux) setRedux(authData)
                navigate('/properties')
            }
        })

        return () => {
            clearQrTimers()
            EventRegister.removeEventListener(socketListener as string)
        }
    }, [])


    if (processing) return <NoData loading />

    return (
        <>
            <style>{`
                .auth-input .MuiOutlinedInput-root {
                    border-radius: 12px;
                }
                .auth-input .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
                    border-color: ${PRIMARY};
                }
                .auth-input label.Mui-focused { color: ${PRIMARY}; }
            `}</style>

            <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#fff' }}>
                {/* ── Left branding panel ── */}
                <Box sx={{
                    display: { xs: 'none', md: 'flex' },
                    width: '42%',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    background: `linear-gradient(160deg, #faf8ee 0%, #e8e4b0 50%, ${PRIMARY} 100%)`,
                    p: 6,
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                    {/* Decorative blobs */}
                    <Box sx={{ position: 'absolute', top: -100, right: -100, width: 350, height: 350, borderRadius: '50%', background: 'rgba(255,255,255,.12)', pointerEvents: 'none' }} />
                    <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 280, height: 280, borderRadius: '50%', background: 'rgba(255,255,255,.08)', pointerEvents: 'none' }} />

                    {/* Logo */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <img src={logo} alt="Sheltergram" style={{ height: 44, width: 44, objectFit: 'contain' }} />
                        <Typography sx={{ fontWeight: 800, fontSize: 22, color: PRIMARY_DARK }}>Sheltergram</Typography>
                    </Box>

                    {/* Headline + features */}
                    <Box>
                        <Typography sx={{ fontSize: { md: 32, lg: 40 }, fontWeight: 800, color: '#1a1a0f', lineHeight: 1.2, mb: 2 }}>
                            The smarter way to manage properties in Kenya.
                        </Typography>
                        <Typography sx={{ fontSize: 16, color: '#4a4218', mb: 5, lineHeight: 1.6 }}>
                            Join thousands of landlords, tenants, and investors already on the platform.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {SIDE_FEATURES.map(f => (
                                <Box key={f.text} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'rgba(255,255,255,.55)', borderRadius: 3, px: 2.5, py: 1.5 }}>
                                    <Typography sx={{ fontSize: 20 }}>{f.icon}</Typography>
                                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#2d2a0a' }}>{f.text}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Typography sx={{ fontSize: 12, color: '#5a5220' }}>
                        © {new Date().getFullYear()} Sheltergram. All rights reserved.
                    </Typography>
                </Box>

                {/* ── Right form panel ── */}
                <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fff',
                    p: { xs: 3, sm: 6 },
                    overflowY: 'auto',
                }}>
                    {/* Mobile logo */}
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, mb: 4 }}>
                        <img src={logo} alt="Sheltergram" style={{ height: 40, width: 40 }} />
                        <Typography sx={{ fontWeight: 800, fontSize: 20, color: PRIMARY }}>Sheltergram</Typography>
                    </Box>

                    <Box sx={{ width: '100%', maxWidth: 440 }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress sx={{ color: PRIMARY }} /></Box>
                        ) : currentPage === 'Sign Up Success' ? (
                            <Box sx={{ textAlign: 'center' }}>
                                <CheckCircleOutline sx={{ fontSize: 64, color: PRIMARY, mb: 2 }} />
                                <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1 }}>You're almost in!</Typography>
                                <Typography sx={{ color: '#6b7280', mb: 4 }}>
                                    {signUpError || 'A confirmation link has been sent to your email with further instructions.'}
                                </Typography>
                                <Button
                                    startIcon={<ArrowBackOutlined />}
                                    onClick={() => setState({ ...state, currentPage: 'Sign In', signUpError: null })}
                                    sx={{ textTransform: 'none', color: PRIMARY, fontWeight: 600 }}
                                >
                                    Back to Sign In
                                </Button>
                            </Box>
                        ) : (
                            <>
                                {/* Page heading */}
                                <Box sx={{ mb: 4 }}>
                                    {currentPage === 'Forgot Password' && (
                                        <IconButton onClick={() => setState({ ...state, currentPage: 'Sign In' })} sx={{ mb: 1, ml: -1 }}>
                                            <ArrowBackOutlined />
                                        </IconButton>
                                    )}
                                    <Typography sx={{ fontSize: { xs: 26, sm: 30 }, fontWeight: 800, color: '#111827' }}>
                                        {currentPage === 'Sign In' ? 'Welcome back' :
                                         currentPage === 'Sign Up' ? 'Create your account' :
                                         currentPage === 'Forgot Password' ? 'Reset your password' :
                                         'Set new password'}
                                    </Typography>
                                    <Typography sx={{ fontSize: 15, color: '#6b7280', mt: 0.5 }}>
                                        {currentPage === 'Sign In' ? 'Sign in to continue to Sheltergram' :
                                         currentPage === 'Sign Up' ? 'Get started — it\'s free' :
                                         currentPage === 'Forgot Password' ? "We'll send a reset link to your email" :
                                         'Choose a strong password'}
                                    </Typography>
                                </Box>

                                {/* Password / other forms */}
                                <Box component="form" onSubmit={handleSubmit}>
                                        <Grid container spacing={2}>
                                            {showInputs.map(({ label, type, field, error }, i) => (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    sm={currentPage === 'Sign Up' && [0, 1].includes(i) ? 6 : 12}
                                                    key={field}
                                                >
                                                    {type === 'checkbox' ? (
                                                        <Fragment>
                                                            <Box sx={{ fontSize: 14 }}>
                                                                <FormControlLabel
                                                                    control={<Checkbox color="primary" name={field} sx={{ '&.Mui-checked': { color: PRIMARY } }} />}
                                                                    label={label}
                                                                    onChange={() => changeInput(field, { error: null })}
                                                                />
                                                                <Link target="_blank" sx={{ ml: -1, color: PRIMARY }} href="/terms_conditions">Terms & Conditions</Link>
                                                                {' '}and{' '}
                                                                <Link target="_blank" href="/privacy_policies" sx={{ color: PRIMARY }}>Privacy Policies</Link>
                                                            </Box>
                                                            {error && <Typography color="error" sx={{ fontSize: 13 }}>*{error}</Typography>}
                                                        </Fragment>
                                                    ) : (
                                                        <TextField
                                                            className="auth-input"
                                                            autoComplete="given-name"
                                                            name={field}
                                                            required
                                                            fullWidth
                                                            label={currentPage === 'Sign In' && field === 'email' ? 'Username or Email' : label}
                                                            type={
                                                                currentPage === 'Sign In' && field === 'email' ? 'text' :
                                                                ['password', 'confirmPassword', 'newPassword'].includes(field)
                                                                    ? (showPassword ? 'text' : type)
                                                                    : (type || 'text')
                                                            }
                                                            autoFocus={i === 0}
                                                            error={Boolean(error)}
                                                            helperText={error}
                                                            onFocus={() => changeInput(field, { error: null })}
                                                            InputProps={{
                                                                endAdornment: ['password', 'confirmPassword', 'newPassword'].includes(field) ? (
                                                                    <InputAdornment position="end">
                                                                        <IconButton onClick={() => setState({ ...state, showPassword: !showPassword })} edge="end">
                                                                            {showPassword ? <VisibilityOffOutlined /> : <RemoveRedEyeOutlined />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ) : undefined,
                                                            }}
                                                        />
                                                    )}

                                                    {currentPage === 'Sign In' && field === 'password' && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                                            <Button
                                                                sx={{ textTransform: 'none', fontSize: 13, color: PRIMARY, p: 0, minWidth: 0 }}
                                                                onClick={() => setState({ ...state, currentPage: 'Forgot Password' })}
                                                            >
                                                                Forgot Password?
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Grid>
                                            ))}
                                        </Grid>

                                        <LoadingButton
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            loading={submitting}
                                            sx={{
                                                mt: 3, mb: 2, textTransform: 'none',
                                                bgcolor: PRIMARY, borderRadius: '12px', py: 1.4,
                                                fontSize: 15, fontWeight: 700,
                                                '&:hover': { bgcolor: PRIMARY_DARK },
                                                '&.MuiLoadingButton-loading': { bgcolor: `${PRIMARY}99` },
                                            }}
                                        >
                                            {currentPage}
                                        </LoadingButton>

                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography component="span" sx={{ fontSize: 14, color: '#6b7280' }}>
                                                {currentPage === 'Sign In' ? "Don't have an account? " : 'Already have an account? '}
                                            </Typography>
                                            <Link
                                                component="button"
                                                type="button"
                                                onClick={() => {
                                                    const next = currentPage === 'Sign In' ? 'Sign Up' : 'Sign In'
                                                    setState({ ...state, currentPage: next, inputs: initialInputs, showPassword: false })
                                                    if (next === 'Sign In') startQrSession()
                                                    else clearQrTimers()
                                                }}
                                                sx={{ fontSize: 14, fontWeight: 700, color: PRIMARY, textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, cursor: 'pointer' }}
                                            >
                                                {currentPage === 'Sign In' ? 'Sign Up' : 'Sign In'}
                                            </Link>
                                        </Box>

                                        {/* QR code — bottom of Sign In */}
                                        {currentPage === 'Sign In' && (
                                            <>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 3 }}>
                                                    <Box sx={{ flex: 1, height: '1px', bgcolor: '#e5e7eb' }} />
                                                    <Typography sx={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>or login with QR code</Typography>
                                                    <Box sx={{ flex: 1, height: '1px', bgcolor: '#e5e7eb' }} />
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, p: 2.5, borderRadius: 3, bgcolor: '#f9f8f4', border: '1px solid #e5e7eb' }}>
                                                    <Box sx={{ flexShrink: 0, p: 1.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 110, height: 110 }}>
                                                        {qrSessionId ? (
                                                            <QRCode value={`https://sheltergram.co/qr-login/${qrSessionId}`} size={88} fgColor={PRIMARY_DARK} />
                                                        ) : (
                                                            <CircularProgress size={32} sx={{ color: PRIMARY }} />
                                                        )}
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: '#111827', mb: 0.8 }}>
                                                            Login with QR Code
                                                        </Typography>
                                                        {['Open the Sheltergram app', 'Go to Profile → Scan QR', 'Point your camera here'].map((step, i) => (
                                                            <Box key={step} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                                <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: PRIMARY, color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                    {i + 1}
                                                                </Box>
                                                                <Typography sx={{ fontSize: 12.5, color: '#6b7280' }}>{step}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    )
}

export default Connect(null, mapDispatchToProps)(Auth)
